import {
  createContext,
  useContext,
  useMemo,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
} from 'react';
import { cva } from 'class-variance-authority';
import { User } from 'lucide-react';
import type { PublicProfile } from '@molt/shared';
import { avatarRosterSignature, disambiguateInitials } from '@/lib/avatar-labels';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { Tooltip } from './Tooltip';
import { initialsSwatch } from './palette';

/**
 * `Avatar` and `AvatarStack` — `FE-04.10`; `card-back.md`'s two rows,
 * `entry-surfaces.md`'s `Avatar` row and C6, `finding-things.md`'s null actor.
 *
 * **Two components in one file**, per `B3-BRIEF.md` §3: `AvatarStack` is not a
 * second implementation of anything — it is `Avatar` laid out, and splitting
 * them would mean two files to read for the overlap rule.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THREE STATES, NOT TWO, AND THE THIRD IS NOT AN EDGE CASE
 * ───────────────────────────────────────────────────────────────────────────
 *
 * | state | what the wire is saying | rendering |
 * |---|---|---|
 * | `normal` | a profile | the image, else the initials on the account's colour |
 * | `unknown` | **the account was deleted** — `ON DELETE SET NULL`, and the server deliberately invents no placeholder name | a neutral disc with a person glyph, named "Deleted account" |
 * | `none` | **there never was an actor** | a neutral disc, `aria-hidden`, announcing nothing |
 *
 * Card back needed the first two (`commentSchema.author` is nullable and means
 * the account is gone). finding-things found that `notificationSchema.actor`
 * is legitimately `null` **four** ways, one of which is `due_soon` — a
 * notification nobody caused. Rendering "Deleted account" beside a due-date
 * reminder would assert that a person existed and left, which is false; and
 * announcing *any* name for it puts a person into a screen reader's ear that
 * the row does not have. So `none` is silent by construction.
 *
 * The prop type makes the pairing exhaustive: `profile: null` **requires** a
 * `state`, and a real profile cannot be labelled `unknown`. A caller cannot
 * express `{ profile: null, state: 'normal' }`, which has no rendering.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * FOUR DEGENERATE VALUES, ALL LEGAL, ALL RENDERED
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `publicProfileSchema` puts a `.max()` and no `.min()` on four fields, and
 * says so deliberately in three of the four docstrings. Every one of them can
 * arrive on a real read:
 *
 *  1. `initialsColour === ''` — the column is `NOT NULL DEFAULT ''`, so any row
 *     not created through the sign-up hook holds it. Falls back to the same
 *     theme-token treatment `colourless` gets (`NO_COLOUR_FALLBACK`), never a
 *     transparent circle.
 *  2. `initials === ''` — a display name of punctuation or emoji yields none.
 *     Falls back to a person glyph, never an empty disc. The initials are NOT
 *     recomputed here: they are server-derived, and a second derivation in the
 *     browser would drift from the one in `apps/server/src/auth/profile.ts`.
 *     The browser still never *derives* initials. Inside an
 *     `AvatarLabelsProvider` it only *separates* the ones that clash within
 *     one roster — one board, one table — so "Benjamin" and "Bert", both `B`
 *     from the server, draw `BE` and `BR` there (`lib/avatar-labels.ts` holds
 *     the rule). Without a provider the server value is drawn untouched, and
 *     the accessible name is the person's name either way.
 *  3. `displayName === ''` — no lower bound either, and the `bio` docstring
 *     next to it makes the argument explicitly ("nothing stops an existing row
 *     holding `''`"). Falls back to `@username`.
 *  4. `username === ''` — same. Falls back to the literal "Member".
 *
 * The name chain is `displayName` → `@username` → `"Member"`, and an avatar
 * that is both blank AND nameless is the failure it exists to prevent.
 * `entry-surfaces.md` C6 names the first two; the third and fourth are this
 * chunk's finding.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE COLOUR IS DATA, NOT THEME
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `users.initials_colour` is per-account data, so it is not recoloured by the
 * theme — the same argument `styles.css` makes for a chosen board background.
 * The ten words map onto the ten families of the ONE palette in `palette.ts`,
 * where the ink is measured per swatch: `yellow` takes dark ink at 9.14:1 and
 * `black` takes light ink at 6.46:1. That is the flip `entry-surfaces.md` C6
 * asks for, though not for C6's stated reason — see the note on
 * `INITIALS_SWATCH_OF`, which records that the tier split is an identity
 * decision and the ink flip follows from it. `avatarUrl`, a real image,
 * overrides all of it.
 */

const avatarVariants = cva(
  'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-medium leading-none',
  {
    variants: {
      size: {
        xs: 'size-5 text-[0.5625rem]',
        sm: 'size-7 text-xs',
        md: 'size-9 text-sm',
      },
      /**
       * Which of the two board inks the initials carry — measured per swatch
       * in `palette.ts`. `theme` is the no-colour fallback and the two null
       * states, which are absences rather than data and so follow the theme.
       */
      ink: {
        dark: 'text-board-ink-dark',
        light: 'text-board-ink-light',
        theme: 'border border-border-strong bg-surface-raised text-ink-muted',
      },
      /**
       * `none` has no person behind it, so it gets no glyph and a hairline
       * rather than a control edge — `--color-border` is decorative-only and
       * this is the one place in the set where that is the correct token.
       */
      empty: {
        true: 'border border-dashed border-border bg-surface-raised',
        false: '',
      },
      /**
       * Affordance only. **`interactive` never changes the element**: both
       * known call sites — the header account menu trigger and a member picker
       * row — already sit inside a control of their own, and an avatar that
       * rendered its own `<button>` could not go in either without nesting one
       * interactive element inside another.
       */
      interactive: {
        true: 'cursor-pointer transition-opacity hover:opacity-85',
        false: '',
      },
    },
    defaultVariants: { size: 'sm', ink: 'theme', empty: false, interactive: false },
  },
);

export type AvatarState = 'normal' | 'unknown' | 'none';
export type AvatarSize = 'xs' | 'sm' | 'md';

interface AvatarOwnProps {
  size?: AvatarSize;
  interactive?: boolean;
  /**
   * The avatar carries no accessible name of its own — for a trigger that is
   * already labelled, such as the header's account menu button
   * (`entry-surfaces.md`'s `Avatar` row).
   */
  decorative?: boolean;
}

type AvatarRest = Omit<
  ComponentPropsWithoutRef<'span'>,
  keyof AvatarOwnProps | 'children' | 'profile' | 'state'
> & { ref?: Ref<HTMLSpanElement> };

export type AvatarProps = AvatarOwnProps &
  AvatarRest &
  (
    | { profile: PublicProfile; state?: 'normal' }
    | {
        profile: null;
        /** Required when there is no profile — the two nulls are different. */
        state: 'unknown' | 'none';
      }
  );

/** What `state='unknown'` is called out loud. The server invents no name; the client must. */
export const DELETED_ACCOUNT_NAME = 'Deleted account';

/** The last resort of the name chain, when every nameable field is the empty string. */
export const ANONYMOUS_MEMBER_NAME = 'Member';

/**
 * The name a screen reader gets for a profile.
 *
 * Exported because it is the same chain a comment byline, a member list row
 * and an activity line all need, and four call sites re-deriving it is how one
 * of them ends up announcing the empty string.
 */
export function profileName(profile: PublicProfile | null): string {
  if (profile === null) return DELETED_ACCOUNT_NAME;
  if (profile.displayName !== '') return profile.displayName;
  if (profile.username !== '') return `@${profile.username}`;
  return ANONYMOUS_MEMBER_NAME;
}

// ---------------------------------------------------------------------------
// Roster-scoped labels
// ---------------------------------------------------------------------------

const NO_LABELS: ReadonlyMap<string, string> = new Map();

const AvatarLabelsContext = createContext<ReadonlyMap<string, string>>(NO_LABELS);

export interface AvatarLabelsProviderProps {
  /**
   * Everyone who can appear as an avatar inside this subtree — a board's
   * members plus every card's members, duplicates allowed. Only clashes
   * *within* it are separated.
   */
  roster: readonly PublicProfile[];
  children: ReactNode;
}

/**
 * Separates clashing initials for every `Avatar` below it.
 *
 * The labels are memoised on `avatarRosterSignature` — a string of exactly
 * the fields the rule reads — and **not** on the `roster` array. A board's
 * roster is rebuilt on every optimistic update and every drag frame; keyed on
 * the array, each of those would hand every avatar on the board a new context
 * value and re-render all of them for a result that cannot have changed.
 */
export function AvatarLabelsProvider({ roster, children }: AvatarLabelsProviderProps) {
  const signature = avatarRosterSignature(roster);
  // `roster` is deliberately not a dependency: `signature` covers every field
  // `disambiguateInitials` reads, so a new array with the same signature has
  // the same answer.
  const labels = useMemo(() => disambiguateInitials(roster), [signature]);
  return (
    <AvatarLabelsContext.Provider value={labels}>{children}</AvatarLabelsContext.Provider>
  );
}

/**
 * The initials to draw for `profile`: the provider's separated label when it
 * has one, otherwise the server's value. Never the accessible name.
 *
 * Accepts `null` — and answers `''` for it — so `Avatar` can call it
 * unconditionally for its two null states rather than repeating the lookup.
 */
export function useAvatarLabel(profile: PublicProfile): string;
export function useAvatarLabel(profile: PublicProfile | null): string;
export function useAvatarLabel(profile: PublicProfile | null): string {
  const labels = useContext(AvatarLabelsContext);
  if (profile === null) return '';
  return labels.get(profile.id) ?? profile.initials;
}

export function Avatar(props: AvatarProps) {
  // `FE-80.1`: the deleted-account name is the one string this component
  // authors itself, so it is the one thing here that needs the catalogue.
  // `profileName` stays translator-free — it has ~45 callers and feeds search
  // haystacks; see the brief §5.3 for why the two spellings are deliberate.
  const { t } = useInterfaceLanguage();
  // `state` is pulled out of the rest so it never lands on the DOM as an
  // attribute; the narrowing below reads it off `props`, where the union still
  // ties it to `profile`.
  const {
    profile,
    size,
    interactive,
    decorative = false,
    className,
    state: _state,
    ...rest
  } = props;
  const state: AvatarState = props.profile === null ? props.state : 'normal';
  const drawn = useAvatarLabel(profile);

  const swatch = profile === null ? null : initialsSwatch(profile.initialsColour);
  const ink = swatch?.ink ?? 'theme';
  // The one place a palette literal leaves `palette.ts` in this file: the
  // value is per-account data read at runtime, so no class name can carry it.
  const fill = swatch ? { backgroundColor: swatch.fill } : undefined;

  // The chain is unchanged — `none` is silent, a deleted account is named, and
  // a real profile still goes displayName → @username → `ANONYMOUS_MEMBER_NAME`
  // through `profileName`. Only the deleted-account branch reads the catalogue,
  // and it cannot render nameless: `t()` on a missing key returns the key, which
  // is non-empty, so rule 10 of the brief's §7 holds even with no stanza at all.
  const name =
    state === 'none'
      ? ''
      : profile === null
        ? t('primitives.avatar.deletedAccount')
        : profileName(profile);
  const labelling =
    decorative || state === 'none'
      ? ({ 'aria-hidden': true } as const)
      : ({ role: 'img', 'aria-label': name } as const);

  return (
    <span
      style={fill}
      className={cn(
        avatarVariants({ size, ink, empty: state === 'none', interactive }),
        className,
      )}
      {...labelling}
      {...rest}
    >
      {state === 'normal' && profile !== null && profile.avatarUrl !== null ? (
        // `alt=""` because the wrapper already carries the name — a described
        // image inside a named `role="img"` announces the person twice.
        <img src={profile.avatarUrl} alt="" className="size-full object-cover" />
      ) : null}

      {state === 'normal' && profile !== null && profile.avatarUrl === null ? (
        drawn === '' ? (
          <User aria-hidden="true" className="size-1/2" />
        ) : (
          <span aria-hidden="true">{drawn}</span>
        )
      ) : null}

      {state === 'unknown' ? <User aria-hidden="true" className="size-1/2" /> : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// AvatarStack
// ---------------------------------------------------------------------------

const OVERFLOW_DEFAULT_MAX = 4;

/**
 * "Ben Edwards · @bedwards"; either half alone when the other is the empty
 * string (both are legal, see above), and the name chain's last resort when
 * both are.
 */
function avatarTooltip(
  member: PublicProfile,
  t: ReturnType<typeof useInterfaceLanguage>['t'],
): string {
  if (member.displayName !== '' && member.username !== '') {
    return t('primitives.avatar.tooltip', {
      name: member.displayName,
      username: member.username,
    });
  }
  return profileName(member);
}

export interface AvatarStackProps extends Omit<
  ComponentPropsWithoutRef<'span'>,
  'children' | 'ref'
> {
  members: readonly PublicProfile[];
  /** How many avatars are drawn before the overflow control. Default 4. */
  max?: number;
  size?: AvatarSize;
  /**
   * ⚠ **`'none'` was deliberately not offered, the card-front chunk asked for
   * it and said why, and this is that answer.** The original note read: *"the
   * same row requires that the overflow chip be a real focusable control, not
   * a static `+3`. Shipping only the focusable form means the card-front chunk
   * has to ask for the other one and say why, rather than reaching for it by
   * default."* The mechanism worked exactly as designed — `C1`'s card front
   * hit the compile error, and the reason it needed `'none'` is stronger than
   * a preference:
   *
   * **The card tile's root is the `<a>` to the card back** (`C1-BRIEF.md`
   * §7.3), so the focusable form puts a `<button>` inside an `<a>`. That is
   * invalid HTML, and it is *functionally* broken rather than merely
   * non-conforming: a click on the overflow chip bubbles to the anchor and
   * navigates to the card back instead of opening the member popover. The
   * focusable control is right on a surface that is not itself one control;
   * the card front is exactly that surface.
   *
   * `'none'` renders the `+3` with no tab stop, and the count still reaches a
   * screen reader — as a sentence in an `sr-only` span, not as a bare `+3`,
   * which is the half of the original requirement that was never about focus.
   * The members it summarises are all on the card back, one click away through
   * the anchor that swallowed the button.
   */
  overflowAction?: 'popover' | 'none';
  /** Overrides the overflow control's accessible name. */
  overflowLabel?: string;
  /**
   * Everything the anchor needs — `onClick`, `aria-expanded`, `aria-controls`,
   * `ref`. Passed as props rather than by importing `Popover`, because the
   * popover is state the caller owns and a primitive that opened its own would
   * be fetching and deciding, which `B3-BRIEF.md` §7 forbids.
   */
  overflowProps?: ComponentPropsWithoutRef<'button'>;
  /**
   * `'tooltip'` wraps each avatar in `Tooltip`, naming the person on hover as
   * "Ben Edwards · @bedwards" (`primitives.avatar.tooltip`). **Opt-in, default
   * `'none'`**: outside a `TooltipProvider`, `Tooltip` mounts a Radix provider
   * per instance, and the tables are not windowed.
   *
   * The card front opts in (member initials, `docs/notes/member-initials.md`).
   * Its tile's stretched `<a>` covers the whole front, so `CardTile` lifts the
   * avatar row above it (`z-index`, never a transform) and forwards the row's
   * click, right-click and drag start to that link; the board face mounts one
   * `TooltipProvider` for every card's tooltips. A caller that puts a stack
   * under a covering element must do the same, or the hover never arrives.
   *
   * Hover only — the avatar is a `span` and gains no tab stop; its accessible
   * name already carries the person's name.
   */
  names?: 'tooltip' | 'none';
  ref?: Ref<HTMLSpanElement>;
}

const overflowVariants = cva(
  'relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full border border-border-strong bg-surface-raised font-medium leading-none text-ink-muted ring-2 ring-surface-raised hover:text-ink',
  {
    variants: {
      size: {
        xs: 'size-5 text-[0.5625rem]',
        sm: 'size-7 text-xs',
        md: 'size-9 text-xs',
      },
    },
    defaultVariants: { size: 'sm' },
  },
);

/**
 * How far each disc in a stack tucks under the one before it, per size.
 *
 * ⚠ **This is a legibility budget, not a look.** The stack used `-ml-2` at
 * every size, and the next disc's 2px `ring-2` paints over the previous one
 * too, so the covered strip was 10px — which hides the second letter of a
 * two-letter label at every size (measured on the card front: Benjamin's `BE`
 * read as `B` beside Bert's `BR`, the exact confusion `avatar-labels.ts`
 * exists to remove). The rule is: the strip covered (`overlap + 2px ring`)
 * must stay right of the drawn text, which is centred and about `1.3em` wide
 * for two capitals.
 *
 *  - `xs` — 20px disc, 9px text ≈ 12px wide, so the text ends near 16px and
 *    the ring alone already takes 18–20px: no overlap at all.
 *  - `sm` — 28px disc, 12px text ≈ 16px wide, ends near 22px: 2px overlap
 *    covers from 24px.
 *  - `md` — 36px disc, 12–14px text ≈ 19px wide, ends near 28px: 4px overlap
 *    covers from 30px.
 *
 * The overflow chip uses the same value, because it paints over the last
 * avatar the same way.
 */
const STACK_OVERLAP: Readonly<Record<AvatarSize, string>> = {
  xs: 'ml-0',
  sm: '-ml-0.5',
  md: '-ml-1',
};

/**
 * A row of overlapping avatars with a real control for the remainder.
 *
 * `max` counts the avatars drawn, not the members; anything past it is
 * summarised by the overflow control. An empty `members` renders nothing at
 * all rather than an empty box — a card with no members should not reserve
 * space for the ones it does not have.
 *
 * The overflow control is a `<button>` with a spoken name ("Show 3 more
 * members"); the visible `+3` is `aria-hidden`, because a bare "+3" read out
 * is not a sentence. `--color-ink-muted` sits on it, which is why the chip is
 * an opaque `--color-surface-raised` and not the translucent panel — muted text
 * needs 98% opacity and the panel's measured floor is 92%.
 */
export function AvatarStack({
  members,
  max = OVERFLOW_DEFAULT_MAX,
  size = 'sm',
  overflowAction = 'popover',
  overflowLabel,
  overflowProps,
  names = 'none',
  className,
  ...rest
}: AvatarStackProps) {
  const { t } = useInterfaceLanguage();

  if (members.length === 0) return null;

  const shown = members.slice(0, max);
  const hidden = members.length - shown.length;
  const { className: overflowClassName, ...overflowRest } = overflowProps ?? {};
  const buttonOverflowLabel =
    overflowLabel ??
    t('primitives.avatarStack.overflow.button', { count: hidden }, hidden);
  const staticOverflowLabel =
    overflowLabel ??
    t('primitives.avatarStack.overflow.static', { count: hidden }, hidden);

  return (
    <span className={cn('inline-flex items-center', className)} {...rest}>
      {shown.map((member, index) => {
        const avatar = (
          <Avatar
            key={member.id}
            profile={member}
            size={size}
            className={cn('ring-2 ring-surface-raised', index > 0 && STACK_OVERLAP[size])}
          />
        );
        if (names !== 'tooltip') return avatar;
        return (
          <Tooltip key={member.id} content={avatarTooltip(member, t)}>
            {avatar}
          </Tooltip>
        );
      })}
      {hidden > 0 && overflowAction === 'popover' ? (
        <button
          type="button"
          aria-label={buttonOverflowLabel}
          className={cn(
            overflowVariants({ size }),
            STACK_OVERLAP[size],
            overflowClassName,
          )}
          {...overflowRest}
        >
          <span aria-hidden="true">+{hidden}</span>
        </button>
      ) : null}
      {/*
        `'none'` — the static chip, for a caller whose whole surface is already
        one control. No tab stop and no `cursor-pointer` (an unclickable thing
        that looks clickable is worse than an honest one), but the count is
        still spoken: the visible `+3` is `aria-hidden` and an `sr-only`
        sentence carries it, which is the requirement the focusable form was
        only one way of meeting.
      */}
      {hidden > 0 && overflowAction === 'none' ? (
        <span
          className={cn(
            overflowVariants({ size }),
            'cursor-default',
            STACK_OVERLAP[size],
            overflowClassName,
          )}
        >
          <span aria-hidden="true">+{hidden}</span>
          <span className="sr-only">{staticOverflowLabel}</span>
        </span>
      ) : null}
    </span>
  );
}
